import { RowData } from '@scope/scope-one-ui/core/DataTable/DataTable.d';
import { ScatterDot } from '@scope/scope-one-ui/core/Charts/ScatterChart/ScatterChart.d';
import {
  PeerGroupComparisionInfo,
  PeerGroupComparisonChartData,
  CompanyScore,
  TopCompanies,
} from '../../types/esgSentiment';

export const getCompareTableData = (data?: PeerGroupComparisionInfo): RowData[] =>
  !data
    ? []
    : [
        {
          id: 'company',
          data: {
            comparison: { name: data.companyInfo.name, type: 'company' },
            scoreOne: data.companyInfo.oneMonth,
            scoreTwo: data.companyInfo.todayScore,
            rangeScore: {
              min: data.companyInfo.minOneYear,
              max: data.companyInfo.maxOneYear,
              value: data.companyInfo.todayScore.value,
            },
          },
        },
        {
          id: 'industry',
          data: {
            comparison: { name: data.industryInfo.name, type: 'industry' },
            scoreOne: data.industryInfo.oneMonth,
            scoreTwo: data.industryInfo.todayScore,
            rangeScore: {
              min: data.industryInfo.minOneYear,
              max: data.industryInfo.maxOneYear,
              value: data.industryInfo.todayScore.value,
            },
          },
        },
        {
          id: 'sector',
          data: {
            comparison: { name: data.sectorInfo.name, type: 'sector' },
            scoreOne: data.sectorInfo.oneMonth,
            scoreTwo: data.sectorInfo.todayScore,
            rangeScore: {
              min: data.sectorInfo.minOneYear,
              max: data.sectorInfo.maxOneYear,
              value: data.sectorInfo.todayScore.value,
            },
          },
        },
      ];

export const getTopCompaniesTableData = (data?: TopCompanies): RowData[] =>
  (data?.result || []).map((item) => ({
    id: item.name,
    data: {
      companyName: item.name,
      scoreOne: { value: item.thirtyDaysScore, change: item.thirtyDaysChange },
      scoreTwo: { value: item.todayScore, change: item.todayChange },
      rangeScore: {
        min: item.yearMinScore,
        max: item.yearMaxScore,
        value: item.todayScore,
      },
    },
  }));

export const chartDataParser = (data: PeerGroupComparisonChartData, colorSet: string[]) => {
  const parse = (company: CompanyScore[], color: string) =>
    company.map(({ score, attentionBuzz, companyName }: CompanyScore) => ({
      x: score,
      y: attentionBuzz,
      name: companyName,
      color,
    }));

  const normalize = (chartData: ScatterDot[]) => {
    const getInterval = (val: number) => Math.ceil((Math.abs(+val) - 200) / 50);

    const buzzScores = chartData.map(({ y }) => +y);
    const positiveInterval = getInterval(Math.max(...buzzScores));
    const negativeInterval = getInterval(Math.min(...buzzScores));

    return chartData.map((item) => ({
      ...item,
      normalizedY: (() => {
        if (item.y > 200) return 200 + (+item.y - 200) / positiveInterval;
        if (item.y < -200) return -200 + (+item.y + 200) / negativeInterval;
        return item.y;
      })() as number,
    }));
  };

  const chartData = normalize(
    parse(data.sectorCompanies, colorSet[3]).concat(
      parse(data.industryCompanies, colorSet[2]),
      parse(data.peerCompanies, colorSet[1]),
      parse(data.actualCompany, colorSet[0]),
    ),
  );

  return chartData;
};
